import { useState, useEffect } from 'react';
import { Button, Input, Select, Space, Table, Tag, Tooltip, Progress } from 'antd';
import { useFetchChargerList } from './Hooks';
import useDebounce from '../../utils/debounce';
import { useOperatorStore } from '../../store/useOperatorStore';
import { useAuthStore } from '../../store/useAuthStore';
import {
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  unixToRelativeTime,
  useIsEditAllowedWithOperator,
  formatUnixToLocalDate,
} from '../../utils/utils';
import { MdRefresh } from 'react-icons/md';
import { useLocation, Link, useNavigate } from 'react-router-dom'; // Import useLocation
import { SearchOutlined } from '@ant-design/icons';
import SingleChargerDrawer from '../locations/[id]/SingleChargerDrawer';
import { LuLogs } from 'react-icons/lu';
import { CSS2, Type2, GBT, Default, Socket } from '../../components/Icons';
import {
  buildOcppLogsSearch,
  resolveOcppLogsOperatorId,
} from '../../utils/ocppLogsNavigation';
const { Option } = Select;

const connectorStatusMap = {
  Available: {
    color: 'green',
    icon: <CheckCircleOutlined />,
    description: 'connector_available',
  },
  Preparing: {
    color: 'orange',
    icon: <ClockCircleOutlined />,
    description: 'connector_preparing',
  },
  Charging: {
    color: 'blue',
    icon: <ThunderboltOutlined className="animate-pulse" />,
    description: 'connector_charging',
  },
  SuspendedEVSE: {
    color: 'red',
    icon: <WarningOutlined />,
    description: 'connector_suspended_evse',
  },
  SuspendedEV: {
    color: 'red',
    icon: <WarningOutlined />,
    description: 'connector_suspended_ev',
  },
  Finishing: {
    color: 'gold',
    icon: <ClockCircleOutlined />,
    description: 'connector_finishing',
  },
  Reserved: {
    color: 'blue',
    icon: <InfoCircleOutlined />,
    description: 'connector_reserved',
  },
  Unavailable: {
    color: 'gray',
    icon: <CloseCircleOutlined />,
    description: 'connector_unavailable',
  },
  Faulted: {
    color: 'red',
    icon: <CloseCircleOutlined />,
    description: 'connector_faulted',
  },
  Ready: {
    color: '',
    description: 'connector_ready',
  },
};

const chargerStatusMap = {
  ONLINE: {
    color: 'green',
    icon: <CheckCircleOutlined />,
    description: 'charger_online',
  },
  OFFLINE: {
    color: 'red',
    icon: <CloseCircleOutlined />,
    description: 'charger_offline',
  },
  CONFIGURING: {
    color: 'orange',
    icon: <ClockCircleOutlined />,
    description: 'charger_configuring',
  },
  FAULTED: {
    color: 'red',
    icon: <WarningOutlined />,
    description: 'charger_faulted',
  },
  READY: {
    color: 'yellow',
    icon: <InfoCircleOutlined />,
    description: 'charger_ready',
  },
  UNKNOWN: {
    color: 'gray',
    icon: <InfoCircleOutlined />,
    description: 'charger_unknown',
  },
};

const expandedRowRender = (record, t) => {
  const connectorColumns = [
    {
      title: t('connectorId'),
      dataIndex: 'connectorId',
      key: 'connectorId',
      align: 'center',
    },
    {
      title: t('connectorType'),
      dataIndex: 'connectorType',
      key: 'connectorType',
      align: 'center',
    },
    // {
    //   title: t('pricePerUnit', {
    //     currency: typeof currency === 'function' ? currency() : currency,
    //   }),
    //   dataIndex: 'pricePerUnit',
    //   key: 'pricePerUnit',
    //   align: 'center',
    //   render: (pricePerUnit) =>
    //     pricePerUnit
    //       ? `${formatCurrency(pricePerUnit)}`
    //       : t('common.notAvailable'),
    // },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status) => {
        if (status === 'Ready') return null;
        const { color, icon, description } = connectorStatusMap[status] || {
          color: 'default',
          description: t('connector_unknown'),
        };
        return (
          <Tooltip title={t(description)}>
            <Tag color={color} icon={icon} variant="outlined">
              {t(status)}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: t('errorCode'),
      dataIndex: 'errorCode',
      key: 'errorCode',
      align: 'center',
      render: (errorCode) => {
        if (errorCode === 'NoError') return null;
        const isOtherError = errorCode === 'OtherError';
        const displayText = isOtherError ? t('OtherError') : errorCode;
        const tooltipTitle = isOtherError ? t('otherErrorTooltip') : null;

        return tooltipTitle ? (
          <Tooltip title={tooltipTitle}>
            <Tag color="red" variant="outlined">{displayText}</Tag>
          </Tooltip>
        ) : (
          <Tag color="red" variant="outlined">{displayText}</Tag>
        );
      },
    },
    {
      title: t('info'),
      dataIndex: 'info',
      key: 'info',
      align: 'center',
      render: (info) => {
        if (info === 'NoError') return null;
        return <div>{info}</div>;
      },
    },
    {
      title: t('vendorErrorCode'),
      dataIndex: 'vendorErrorCode',
      key: 'vendorErrorCode',
      align: 'center',
      render: (vendorErrorCode) => {
        if (vendorErrorCode === 'NoError' || vendorErrorCode === '0')
          return null;
        return (
          vendorErrorCode && (
            <Tag color="red" variant="outlined">
              <Link to={`/dashboard/diagnostic?ErrorCode=${vendorErrorCode}`}>
                {vendorErrorCode}
              </Link>
            </Tag>
          )
        );
      },
    },
  ];

  return (
    <Table
      columns={connectorColumns}
      dataSource={record.connectors}
      pagination={false}
      rowKey={(record) => record._id}
    />
  );
};

const ChargersList = () => {
  const { t } = useTranslation(['ChargerList', 'common']);
  const location = useLocation(); // Initialize useLocation
  const queryParams = new URLSearchParams(location.search); // Parse query parameters
  const initialStatus = queryParams.get('status'); // Get the 'status' query parameter
  const isEditAllowed = useIsEditAllowedWithOperator();
  const [searchQuery, setSearchQuery] = useState('');

  const debouncedSearch = useDebounce(searchQuery, 300);
  const [status, setStatus] = useState(initialStatus || 'ONLINE'); // Initialize with query parameter
  const [page, setPage] = useState(1);
  const limit = 10;
  const { operatorId, name } = useOperatorStore();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const {
    data: chargers,
    isLoading,
    refetch,
    isFetching,
  } = useFetchChargerList(
    { limit, search: debouncedSearch, offset: page },
    operatorId,
    status
  );

  const openLocationDrawer = (locationId) => {
    setIsDrawerOpen(true);
    setSelectedLocation(locationId);
  };

  // Close Drawer
  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedLocation(null);
  };

  const columns = [
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      fixed: 'left',
      width: 300,
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => (
        <span onClick={() => openLocationDrawer(record._id)} className='cursor-pointer whitespace-pre-wrap hover:text-[#28A745]'>
          {`${text} - (${record.maxCapacity} kW)`}
        </span>
      )
    },
    {
      title: t('serialNumber'),
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      width: 120,
      align: 'center',
    },
    {
      title: t('operatorName'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      align: 'center',
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: t('locationName'),
      dataIndex: 'locationData',
      key: 'locationData',
      ellipsis: {
        showTitle: false,
      },
      align: 'center',
      render: (locationData) => {
        const locationName = locationData?.[0]?.locationName;
        return (
          <Tooltip
            className="!text-capitalize"
            placement="topLeft"
            title={locationName}
          >
            {locationName}
          </Tooltip>
        );
      },
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: 140,
      ellipsis: {
        showTitle: false,
      },
      render: (status, record) => {
        const { color, icon } =
          chargerStatusMap[status] || chargerStatusMap.UNKNOWN;

        // Get time information
        let timeText = '';
        let timeLabel = '';

        if (status === 'ONLINE') {
          timeText = record?.lastOnline
            ? unixToRelativeTime(record.lastOnline)
            : t('noRecentUsage');
          timeLabel = t('onlineSince');
        } else {
          timeText = record?.lastOffline
            ? formatUnixToLocalDate(record.lastOffline)
            : (record?.lastOnline
              ? formatUnixToLocalDate(record.lastOnline)
              : t('noRecentUsage'));
          timeLabel = t('offlineSince');
        }

        const hasConnectionIssue = record.ocppConnectionIssue && record.ocppConnectionIssue !== 'NO';

        return (
          <div className="flex flex-col items-center">
            {/* Status with Icon */}
            <div className="flex items-center gap-1 mb-1">
              <Tag color={color} icon={icon} className="capitalize text-xs" variant="outlined">
                {t(status, { defaultValue: status })}
              </Tag>
              {hasConnectionIssue && (
                <Tooltip title={record.ocppConnectionIssue}>
                  <WarningOutlined className="text-red-500 text-xs" />
                </Tooltip>
              )}
            </div>

            {/* Time Information */}
            <Tooltip title={
              status === 'ONLINE'
                ? `${timeLabel}: ${record?.lastOnline ? formatUnixToLocalDate(record.lastOnline) : t('noRecentUsage')}`
                : `${timeLabel}: ${timeText}`
            }>
              <div className="text-xs text-gray-500 cursor-help">
                {timeText}
              </div>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: t('todayUptime',),
      dataIndex: 'todayUptimePercentage',
      key: 'todayUptimePercentage',
      align: 'center',
      ellipsis: {
        showTitle: false,
      },
      width: 120,
      render: (value) => {
        if (typeof value !== 'number') {
          return (
            <Tooltip title={t('noDataForToday', 'No data for today')}>
              <span style={{ color: '#aaa', fontSize: 18 }}>—</span>
            </Tooltip>
          );
        }
        let color = '#52c41a'; // green
        if (value < 80) color = '#ff4d4f'; // red
        else if (value < 95) color = '#faad14'; // orange
        return (
          <Tooltip title={`${value.toFixed(0)}%`}>
            <Progress
              type="circle"
              percent={value}
              size={30}

              strokeColor={color}
              format={p => `${p?.toFixed(0)}%`}
              strokeWidth={6}
            />
          </Tooltip>
        );
      },
    },
    {
      title: t('connectorId'),
      key: 'connectorStatus',
      align: 'center',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <div className="flex items-center gap-2">
          {record?.connectors
            ?.filter((connector) => connector?.status !== 'Ready')
            .map((connector) => {
              const connectorImages = {
                TYPE2: <Type2 className="size-8" status={connector.status} />,
                CCS2: <CSS2 className="size-8" status={connector.status} />,
                GBT: <GBT className="size-6" status={connector.status} />,
                Socket: <Socket className="size-6" status={connector.status} />,
              };
              const connectorImage = connectorImages[
                connector?.connectorType
              ] || <Default className="size-6" status={connector?.status} />;
              return (
                <Tooltip
                  key={connector.connectorId}
                  title={`${connector.connectorType || 'Unknown'} - ${connector.connectorId
                    } - ${connector.status}`}
                >
                  {connectorImage}
                </Tooltip>
              );
            })}
        </div>
      ),
    },
    {
      title: t('Action'),
      key: 'action',
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <div className="flex gap-2 items-center justify-center">
          <Tooltip title={t('viewCharger')}>
            <Button
              type="default"
              icon={<EyeOutlined />}
              onClick={() => {
                openLocationDrawer(record._id);
              }}
            />
          </Tooltip>
          {isEditAllowed && (
            <Tooltip title={t('viewOcppLogs')} placement='left' >
              <Button
                type="default"
                icon={<LuLogs />}
                onClick={() => {
                  const ocppOperatorId = resolveOcppLogsOperatorId(
                    operatorId,
                    record
                  );
                  navigate({
                    pathname: '/dashboard/OCPP-logs',
                    search: buildOcppLogsSearch({
                      chargerId: record._id,
                      chargerName: record.name ?? '',
                      operatorId: ocppOperatorId,
                    }),
                    state: {
                      charger: {
                        id: record._id,
                        name: record.name ?? '',
                      },
                      operatorId: ocppOperatorId,
                    },
                  });
                }}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const refreshData = () => {
    refetch();
  };

  useEffect(() => { }, [searchQuery, status]);

  useEffect(() => {
    if (initialStatus) {
      setStatus(initialStatus); // Update status if query parameter exists
    }
  }, [initialStatus]);

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Space
        orientation="horizontal"
        size="large"
        className="w-full justify-between flex flex-col md:flex-row"
        breakpoint="sm"
      >
        <Space orientation="horizontal" size="middle" wrap>
          <Input
            allowClear
            placeholder={t('searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={searchQuery}
            style={{ width: 300 }}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (page !== 1) setPage(1);
            }}
          />
          <Select
            allowClear
            className="max-w-xs w-[175px]"
            placeholder={t('selectStatus')}
            value={status}
            onChange={(value) => {
              setStatus(value);
              if (page !== 1) setPage(1);
            }}
          >
            {[
              'ONLINE',
              'OFFLINE',
              'FAULTED',
              'READY',
            ].map((value) => (
              <Option key={value} value={value}>
                {t(value, { defaultValue: value.charAt(0) + value.slice(1).toLowerCase() })}
              </Option>
            ))}
          </Select>
          <Button
            className="bg-gray-200 text-gray-800 hover:bg-gray-300"
            onClick={() => {
              setSearchQuery(null);
              setStatus(null);
            }}
          >
            {t('clear')}
          </Button>
          <Button
            icon={<MdRefresh />}
            loading={isFetching}
            onClick={refreshData}
          ></Button>
        </Space>
        <Space orientation="horizontal" size="middle">
          {operatorId &&
            (user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') && (
              <div className="flex items-center justify-end ">
                <Tooltip
                  title={t('dashboard:alertMessage', {
                    operatorName: name,
                  })}
                >
                  <Tag
                    className="flex items-center"
                    icon={<InfoCircleOutlined />}
                    color="green"
                    variant="outlined"
                    style={{
                      background: '#f6ffed',
                      borderColor: '#b7eb8f',
                      color: '#389e0d',
                      fontWeight: 600,
                    }}
                  >
                    {name}{' '}
                  </Tag>
                </Tooltip>
              </div>
            )}
        </Space>
      </Space>

      <Table
        loading={isLoading}
        locale={{ emptyText: t('common:noData') }}
        columns={columns}
        dataSource={chargers?.data?.paginatedData}
        expandable={{
          expandedRowRender: (record) => expandedRowRender(record, t),
        }}
        size="small"
        variant="outlined"
        scroll={{ x: 'max-content', y: 60 * 8.3 }}
        pagination={{
          pageSize: limit,
          current: page,
          total: chargers?.data?.totalCount || 0,
          onChange: (page) => setPage(page),
          position: ['bottomCenter'],
          showSizeChanger: false,
          size: 'default',
        }}
        rowKey={(record) => record._id}
      />
      {isDrawerOpen && (
        <SingleChargerDrawer
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
          selectedCharger={selectedLocation}
        />
      )}
    </Space>
  );
};

export default ChargersList;
